<template>
  <div class='condition-row'>
    <div class='condition-row__full-condition'>
      <div class='condition-row__full-condition__actions' v-if='condition.children'>
        <v-menu
          :close-on-content-click='true'
          rounded
          offset-x
          :nudge-width="25"
          bottom
          right
        >
          <template v-slot:activator="{ attrs, on }">
            <div class="add-new-condition-section" v-bind="attrs" v-on="on">
              <div class="add-new-condition-header">
                <label  class="add-new-condition-add-button">
                  <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
                </label>
              </div>
            </div>
          </template>
          <v-list
            class="profile-menu"
          >
            <v-list-item
              v-for="(item, index) in addConditionMenuOptions"
              :key="index"
              @click='menuOptionSelected(item, condition.id)'
            >
              <v-list-item-content>
                <v-list-item-title v-text="item" />
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class='condition-row__full-condition__brackets-right' v-if='condition.children'>
        (
      </div>
      <div class='condition-row__full-condition__main'>
        <div class='condition-row__full-condition__main__content'>
          <v-combobox
            class='condition-field'
            :items='conditionsOptionsFormatted'
            v-model='condition.condition'
            :label='$t("condition.input.field.label")'
            :rules='[rules.required]'
            outlined
            dense
          />
          <v-combobox
            class='operator-field'
            :items='operatorOptions'
            v-model='condition.operator'
            :label='$t("operator.input.field.label")'
            :rules='[rules.required]'
            outlined
            dense
          />
          <v-text-field
            class='value-field'
            v-model='condition.value'
            :label='$t("value.input.field.label")'
            :rules='[rules.required]'
            outlined
            dense
          />
          <div class='delete-row-section'>
            <custom-tooltip popper-class="light-tooltip" placement="right-end">
              <div slot='buttonContent'>
                <span
                  class="delete-icon material-icons"
                  :class='{"disabled": ((conditionIndex === 0 && conditionsList.length > 1) || (condition.children && condition.children.length > 0))}'
                  @click="deleteCurrentItem([condition.id])"
                >
                  highlight_off
                </span>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{$t('delete')}}
                </slot>
              </div>
            </custom-tooltip>
          </div>
        </div>
        <div class="condition-row__full-condition__main__relation" v-if='hasNextRelation'>
          <v-chip-group
            v-model='condition.relation'
            column
          >
            <v-chip
              small
              filter
              outlined
              v-for='(relationOption, relationIndex) in relationOptions' :key='relationIndex' :value='relationOption'
            >
              {{ relationOption }}
            </v-chip>
          </v-chip-group>
        </div>
        <div class='condition-row__full-condition__main__children' v-if="condition.children && condition.children.length">
          <condition-row
            v-for="(child, childIndex) in condition.children"
            :key=childIndex
            :has-next-relation='calculateHasNextRelation(child, childIndex)'
            :has-group-relation='calculateHasGroupRelation(child, childIndex)'
            :conditions-list='conditionsList'
            :condition="child"
            :condition-options='conditionOptions'
            @deleteCurrentItem='deleteCurrentItem'
            @addCondition='addCondition'
            @addConditionGroup='addConditionGroup'
          />
        </div>
      </div>
      <div class='condition-row__full-condition__brackets-left' v-if='condition.children'>
        )
      </div>
    </div>
    <div class="condition-row__group-relation" v-if='hasGroupRelation'>
      <v-chip-group
        v-model='condition.groupRelation'
        column
      >
        <v-chip
          small
          filter
          outlined
          v-for='(relationOption, relationIndex) in relationOptions' :key='relationIndex' :value='relationOption'
        >
          {{ relationOption }}
        </v-chip>
      </v-chip-group>
    </div>
  </div>
</template>

<script>
import { OperatorOptions } from '@/constants/conditions';
import { rules } from '@/utils/validationUtils';
import CustomTooltip from '@/components/common/CustomTooltip.vue';

export default {
  name: 'ConditionRow',
  components: {CustomTooltip},
  props: {
    hasGroupRelation: {
      default: false,
      required: false,
      type: Boolean,
    },
    hasNextRelation: {
      default: false,
      required: false,
      type: Boolean,
    },
    baseMargin: {
      type: Number,
      required: false,
      default: 15,
    },
    conditionsList: {
      required: true,
      type: Array,
      default: () => [],
    },
    conditionOptions: {
      required: true,
      type: Array,
      default: () => [],
    },
    condition: {
      required: true,
    },
  },
  methods: {
    calculateHasGroupRelation(condition, index) {
      return condition.children !== undefined && index < this.condition.children.length - 1;
    },
    calculateHasNextRelation(condition, index) {
      if (condition.children !== undefined) {
        if (condition.children.length > 0) {
          return true;
        }
        return false;
      }
      return index < this.condition.children.length - 1;
    },
    menuOptionSelected(item, conditionId) {
      if (item === this.$t('add.condition.group.option')) {
        this.addConditionGroup([conditionId]);
      } else {
        this.addCondition([conditionId]);
      }
    },
    addConditionGroup(conditionIds) {
      let updatedConditionIds = conditionIds;
      if (conditionIds.length > 0 && this.condition.id === conditionIds[0]) {
        updatedConditionIds = [this.condition.id];
      } else {
        updatedConditionIds = [this.condition.id, ...conditionIds];
      }
      this.$emit('addConditionGroup', updatedConditionIds);
    },
    addCondition(conditionIds) {
      let updatedConditionIds = conditionIds;
      if (conditionIds.length > 0 && this.condition.id === conditionIds[0]) {
        updatedConditionIds = [this.condition.id];
      } else {
        updatedConditionIds = [this.condition.id, ...conditionIds];
      }
      this.$emit('addCondition', updatedConditionIds);
    },
    deleteCurrentItem(conditionIds) {
      let updatedConditionIds = conditionIds;
      if (conditionIds.length > 0 && this.condition.id === conditionIds[0]) {
        updatedConditionIds = [this.condition.id];
      } else {
        updatedConditionIds = [this.condition.id, ...conditionIds];
      }
      this.$emit('deleteCurrentItem', updatedConditionIds);
    },
  },
  computed: {
    rules() {
      return {...rules};
    },
    conditionIndex() {
      return this.conditionsList.findIndex(condition => this.condition.id === condition.id);
    },
    addConditionMenuOptions() {
      return [
        this.$t('add.condition.group.option'),
        this.$t('add.single.condition.option'),
      ];
    },
    relationOptions() {
      return [this.$t('and.relation.option'), this.$t('or.relation.option')];
    },
    conditionsOptionsFormatted() {
      return this.conditionOptions;
    },
    operatorOptions() {
      return [...Object.keys(OperatorOptions)].map(operator => this.$t(`condition.${operator}.label`));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.condition-row {
  display: flex;
  flex-direction: column;

  &__full-condition {
    display: flex;
    align-items: center;

    &__main {
      display: flex;
      flex-direction: column;

      &__children {
      }

      &__relation {
        align-items: center;
        justify-content: center;
        margin-left: 10%;
        width: fit-content;
        font-size: $font-size-sm;
      }

      &__group-relation {
        align-items: center;
        justify-content: center;
        margin-left: rem(-10);
        max-width: rem(70);
        font-size: $font-size-sm;
      }

      &__content {
        display: flex;
        margin-top: $space-4;

        .condition-field {
          margin-right: $space-2;
          max-width: rem(200);
        }

        .operator-field {
          margin-right: $space-2;
          max-width: rem(200);
        }

        .value-field {
          max-width: rem(200);
        }

        .delete-row-section {
          display: flex;
          align-items: center;
          margin-left: $space-4;

          .delete-icon {
            color: var(--status-error);
            cursor: pointer;
            font-size: $font-size-md;
            margin-left: $space-1;
            margin-bottom: $space-1;
            font-weight: bolder;

            &.disabled {
              color: var(--text-muted);
              cursor: auto;
            }
          }
        }
      }
    }

    &__actions {
      display: flex;
      flex-direction: column;
      align-self: flex-start;
      margin-right: rem(20);
      margin-top: rem(22);

      .add-new-condition-section {
        margin-left: $space-4;
        display: flex;
        align-items: center;

        .add-new-condition-header {
          margin-top: $space-3;
        }

        .add-new-condition-add-button {
          color: var(--text-secondary);
          text-transform: uppercase;
          font-size: $font-size-sm;
          font-weight: 500;
          letter-spacing: 1.07px;
          line-height: rem(16);
          text-align: center;
          margin-right: $space-3;
          cursor: pointer;

          .plus {
            font-size: $font-size-md;
          }
        }

        &:hover {
          cursor: pointer;
        }
      }
    }

    &__brackets-right {
      display: flex;
      flex-direction: column;
      align-self: flex-start;
      margin-right: rem(20);
      margin-top: $space-4;
      font-size: rem(40);
      color: var(--text-secondary);
    }

    &__brackets-left {
      display: flex;
      flex-direction: column;
      margin-left: rem(20);
      align-self: flex-end !important;
      margin-bottom: $space-2;
      font-size: rem(40);
      color: var(--text-secondary);
    }
  }

  &__group-relation {
    align-items: center;
    justify-content: center;
    margin-left: 15%;
    width: fit-content;
    font-size: $font-size-sm;
  }
}
</style>
